import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { Button, Icons } from '@components/common';
import FlatTreeXML from '@components/common/FlatXMLTree/FlatTreeXML';

import { useQueryNetconfPreviewDataById } from '@hooks/vesEvents/useQueryNetconfRawDataById';

import { TMapTree } from '@components/common/FlatXMLTree/interface';

import { COPY_TIMEOUT } from '@constants/common';

import styles from './PreviewNetconfEvent.module.scss';

interface IPreviewNetconfEventProps {
	onClose: () => void;
	id: string;
}

const PreviewNetconfEvent = ({
	onClose,
	id,
}: IPreviewNetconfEventProps) => {
	const [treeData, setTreeData] = useState<TMapTree>(new Map());
	const [isCopied, setIsCopied] = useState(false);

	const {
		netconfEventParsedData,
		isLoadingNetconfEventData,
		netconfData,
	} = useQueryNetconfPreviewDataById({ id });

	const handleCopyClick = useCallback(() => {
		if (!netconfData?.text) {
			return;
		}

		navigator.clipboard
			.writeText(netconfData.text)
			.then(() => {
				setIsCopied(true);
				setTimeout(() => setIsCopied(false), COPY_TIMEOUT);
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('Failed to copy: ', err);
			});
	}, [netconfData?.text]);

	useEffect(() => {
		if (netconfEventParsedData) {
			setTreeData(netconfEventParsedData);
		}
	}, [netconfEventParsedData]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.treeWrapper}>
				{netconfData?.isXml ? (
					<FlatTreeXML
						data={treeData}
						isLoading={isLoadingNetconfEventData}
						isReadOnly
						setData={setTreeData}
					/>
				) : (
					<div className={styles.textWrapper}>
						{netconfData?.text}
					</div>
				)}
			</div>
			<div className={styles.buttonWrapper}>
				<div
					className={cn(styles.copyButtonContainer, {
						[styles.copiedState]: isCopied,
					})}
					onClick={handleCopyClick}
				>
					{isCopied ? Icons.checkMark() : Icons.copy()}
					<span>
						{isCopied ? 'Copied' : 'Copy'}
					</span>
				</div>
				<Button
					large
					onClick={onClose}
					text='Close'
				/>
			</div>
		</div>
	);
};

export default PreviewNetconfEvent;
